import React, {useRef, useState, useEffect} from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

// Add custom styles for larger touch targets
const customStyles = `
  .mySwiper .swiper-button-next,
  .mySwiper .swiper-button-prev {
    width: 48px !important;
    height: 48px !important;
    margin-top: -24px !important;
  }
  
  .mySwiper .swiper-button-next::after,
  .mySwiper .swiper-button-prev::after {
    font-size: 20px !important;
    font-weight: bold !important;
  }
`;
import {Swiper, SwiperSlide} from "swiper/react";
import {FreeMode, Navigation, Pagination, Thumbs} from "swiper/modules";
import {Box} from "@mui/material";

interface Video {
    id: string;
    title: string;
    url: string;
    thumbnail?: string;
    description?: string;
}

// Helper function to detect and extract Vimeo video ID
const getVimeoId = (url: string): string | null => {
    const vimeoRegex = /(?:vimeo\.com\/)(?:video\/)?(\d+)/;
    const match = url.match(vimeoRegex);
    return match ? match[1] : null;
};

interface VideoSlideshowProps {
    videos: Video[];
}

function VideoSlideshow({ videos }: VideoSlideshowProps) {
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [mainSwiper, setMainSwiper] = useState(null);
    const [videoPaused, setVideoPaused] = useState<boolean[]>([]);
    const [activeSlide, setActiveSlide] = useState(0);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const pipTriggerRef = useRef<HTMLDivElement>(null);

    const handleThumbnailClick = (index: number) => {
        if (mainSwiper) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            mainSwiper.slideTo(index);
            setActiveSlide(index);
            
            // Stop all videos first
            videoRefs.current.forEach(video => {
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                }
            });
            
            // Start playing the selected video
            setTimeout(() => {
                const selectedVideo = videoRefs.current[index];
                if (selectedVideo) {
                    selectedVideo.play().catch(() => {});
                }
            }, 100);
        }
    };

    const handleVideoEnded = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
        }
    };

    const handleOverlayClick = (index: number) => {
        const video = videoRefs.current[index];
        if (video) {
            if (video.paused) {
                video.play().catch(() => {});
            } else {
                video.pause();
            }
        }
    };

    useEffect(() => {
        // Initialize paused state for all videos
        setVideoPaused(new Array(videos.length).fill(true));
        
        // Add event listeners to track play/pause state and PiP events
        const cleanupFunctions: (() => void)[] = [];
        
        videos.forEach((_, index) => {
            const video = videoRefs.current[index];
            if (video) {
                const handlePlay = () => {
                    setVideoPaused(prev => {
                        const newState = [...prev];
                        newState[index] = false;
                        return newState;
                    });
                };
                
                const handlePause = () => {
                    setVideoPaused(prev => {
                        const newState = [...prev];
                        newState[index] = true;
                        return newState;
                    });
                };
                
                video.addEventListener('play', handlePlay);
                video.addEventListener('pause', handlePause);
                video.addEventListener('enterpictureinpicture', handlePiPEnter);
                video.addEventListener('leavepictureinpicture', handlePiPExit);
                
                cleanupFunctions.push(() => {
                    video.removeEventListener('play', handlePlay);
                    video.removeEventListener('pause', handlePause);
                    video.removeEventListener('enterpictureinpicture', handlePiPEnter);
                    video.removeEventListener('leavepictureinpicture', handlePiPExit);
                });
            }
        });

        return () => {
            cleanupFunctions.forEach(cleanup => cleanup());
        };
    }, [videos]);

    // Simple PiP trigger observer
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            const currentVideo = videoRefs.current[activeSlide];
            
            if (!currentVideo || currentVideo.paused) {
                return;
            }
            
            const isVisible = entry.isIntersecting;
            
            if (!isVisible && !document.pictureInPictureElement) {
                // Video is playing but not visible - try PiP, pause if fails
                if ('pictureInPictureEnabled' in document && currentVideo.requestPictureInPicture) {
                    currentVideo.requestPictureInPicture()
                        .catch(() => {
                            currentVideo.pause();
                        });
                } else {
                    currentVideo.pause();
                }
            } else if (isVisible && document.pictureInPictureElement) {
                // Video is visible again - exit PiP
                document.exitPictureInPicture()
                    .catch(() => {});
            }
        }, {
            threshold: 0.1,
            rootMargin: '50px'
        });

        if (pipTriggerRef.current) {
            observer.observe(pipTriggerRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [activeSlide]);



    // Handle Picture-in-Picture events
    const handlePiPEnter = () => {
        // PiP entered
    };

    const handlePiPExit = () => {
        // PiP exited
    };

    return (
        <div style={{position: 'relative'}}>
            <style>{customStyles}</style>
            
            {/* Invisible PiP trigger div positioned over the video */}
            <div 
                ref={pipTriggerRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '200px', // Covers the video area
                    zIndex: 1,
                    pointerEvents: 'none'
                }}
            />
            
            <Swiper
                pagination={{type: 'progressbar',}}
                loop={false}
                spaceBetween={10}
                thumbs={{swiper: thumbsSwiper}}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onSwiper={setMainSwiper}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
                modules={[FreeMode, Thumbs, Pagination]}
                className="mySwiper2"
            >
                {videos.map((video: Video, index: number) => {
                    const vimeoId = getVimeoId(video.url);
                    const isVimeo = vimeoId !== null;

                    return (
                        <SwiperSlide key={`video-${video.id}-${index}`}>
                            <Box component={'div'}
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    aspectRatio: '16/9',
                                    backgroundColor: '#000',
                                    color: 'white',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                }}
                            >
                                {isVimeo ? (
                                    <iframe
                                        src={`https://player.vimeo.com/video/${vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: 0
                                        }}
                                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        title={video.title}
                                    />
                                ) : (
                                    <>
                                        <video
                                            ref={el => videoRefs.current[index] = el}
                                            controls
                                            controlsList="nodownload"
                                            onEnded={handleVideoEnded}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain'
                                            }}
                                            poster={video.thumbnail}
                                            preload="auto"
                                        >
                                            <source src={video.url+"#t=0.1"} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>

                                        {/* Overlay for play/pause control - only for non-Vimeo videos */}
                                        <Box
                                            onClick={() => handleOverlayClick(index)}
                                            sx={{
                                                position: 'absolute',
                                                top: '20%',
                                                left: 0,
                                                right: 0,
                                                bottom: '20%',
                                                backgroundColor: 'transparent',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                opacity: videoPaused[index] ? 1 : 0,
                                                transition: 'opacity 0.3s ease',
                                                '&:hover': {
                                                    opacity: videoPaused[index] ? 1 : 0.3,
                                                }
                                            }}
                                        >
                                            {videoPaused[index] && (
                                                <Box
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'white',
                                                        fontSize: '32px',
                                                    }}
                                                >
                                                    ▶
                                                </Box>
                                            )}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
            <Box component={'div'} sx={{my: 2}} />
            <Swiper
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onSwiper={setThumbsSwiper}
                spaceBetween={15}
                slidesPerView={'auto'}
                freeMode={true}
                watchSlidesProgress={true}
                navigation={true}
                breakpoints={{
                    320: {
                        slidesPerView: 2,
                        spaceBetween: 15,
                    },
                    640: {
                        slidesPerView: 3,
                        spaceBetween: 18,
                    },
                    768: {
                        slidesPerView: 4,
                        spaceBetween: 20,
                    },
                }}
                modules={[FreeMode, Thumbs, Navigation]}
                className="mySwiper"
                style={{
                    '--swiper-navigation-color': '#ffffff',
                    '--swiper-navigation-size': '32px',
                } as React.CSSProperties}
            >
                {videos.map((video: Video, index: number) => (
                    <SwiperSlide key={`thumbnail-${video.id}-${index}`}>
                        <Box component={'div'}
                             onClick={() => handleThumbnailClick(index)}
                             sx={{
                                 position: 'relative',
                                 flex: '1 1 auto',
                                 minWidth: { xs: '100px', md: '100px' },
                                 height: { xs: '100px', md: '200px' },
                                 backgroundColor: '#000',
                                 backgroundImage: video.thumbnail ? `url(${video.thumbnail})` : 'none',
                                 backgroundSize: 'cover',
                                 backgroundPosition: 'center',
                                 color: 'white',
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 cursor: 'pointer',
                                 border: activeSlide === index ? '4px solid #333' : 'none',
                                 borderRadius: '8px',
                                 opacity: activeSlide === index ? 1 : 0.7,
                                 transition: 'all 0.3s ease',
                                 overflow: 'hidden',
                                 '&:hover': {
                                     opacity: 1,
                                 },
                                 '&::after': video.thumbnail ? {} : {
                                     content: '"▶"',
                                     fontSize: '24px',
                                     color: 'white',
                                 },
                             }}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
            
        </div>
    );
}

export default VideoSlideshow;